/**
 * Diagnostic telemetry for DR-DRAWING-VA (intermittent "RuntimeError: null function"
 * inside app.Update()). The crash itself happens inside the compiled WASM module with
 * no visibility from the JS stack, so this records the JS-side state leading up to each
 * update() call and attaches it to Sentry — the goal is to confirm whether the crash
 * correlates with rendering shortly after a graphics mutation/selection change, across
 * all production occurrences rather than just the couple of session replays we could
 * inspect by hand.
 */
import { Logger } from "@/logger";

// update()/throttledUpdate() sources that fire every frame (rAF loop, drag-move).
// Breadcrumbing these would flood the trail and bury the signal from one-off calls
// (dragger onEnd, text edits, etc.), so only the context snapshot is refreshed for them.
const HIGH_FREQUENCY_SOURCES = new Set(["animate", "throttledUpdate"]);

let lastGraphicsModifiedAt: number | null = null;
let lastGraphicsModifiedEntityType: string | null = null;
let lastRegenAllAt: number | null = null;
let lastSelectionCount = 0;

/** Call right after marking an entity's graphics dirty (recordGraphicsModified). */
export function noteGraphicsModified(entityType: string): void {
  lastGraphicsModifiedAt = performance.now();
  lastGraphicsModifiedEntityType = entityType;
  Logger.breadcrumb("graphics-modified", { entityType });
}

/** Call right after app.regenAll() runs. */
export function noteRegenAll(source: string): void {
  lastRegenAllAt = performance.now();
  Logger.breadcrumb("regen-all", { source });
}

/** Call right after the native selection set changes. */
export function noteSelectionChanged(count: number, source: string): void {
  lastSelectionCount = count;
  Logger.breadcrumb("selection-changed", { count, source });
}

/**
 * Snapshot current state onto the Sentry scope right before app.Update().
 * Safe to call on every frame: setContext overwrites rather than accumulating.
 */
export function captureUpdateTelemetry(source: string): void {
  const now = performance.now();
  Logger.setContext("drawing_update", {
    source,
    msSinceLastGraphicsModified:
      lastGraphicsModifiedAt !== null
        ? Math.round(now - lastGraphicsModifiedAt)
        : null,
    msSinceLastRegenAll:
      lastRegenAllAt !== null ? Math.round(now - lastRegenAllAt) : null,
    lastModifiedEntityType: lastGraphicsModifiedEntityType,
    selectionCount: lastSelectionCount,
  });

  if (!HIGH_FREQUENCY_SOURCES.has(source)) {
    Logger.breadcrumb("viewer-update", { source });
  }
}
